import { ChangeEvent, useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { KnmErknmStatusIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusIdsByCodeEnum';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import {
  getUserRoleIsOmbudsman,
  getUserRoleIsSupervisor,
  getUserProfileIsTest,
  getUserRoleIsProsecutor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { ROUTE_PM_NEW } from 'CorePackage/MainModule/routes/routes';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { addNoticeImportNotification } from 'CorePackage/NotificationModule/common/addNoticeImportNotification';
import { pmListFilterSetSwitcher } from 'PreventionPackage/PmListFiltersModule/actions/pmListFilterActions';
import { preventionGetKnms } from 'PreventionPackage/MainModule/actions/preventionActions';
import { mapPmFilterToRaw } from 'PreventionPackage/PmListFiltersModule/common/mapPmFilterToRaw';
import { IPmListFilter } from 'PreventionPackage/PmListFiltersModule/interfaces/IPmListFilter';
import { getPmListFilterCurrentFilter } from 'PreventionPackage/PmListFiltersModule/selectors/pmListFilterSelectors';
import { PmListFiltersModal } from 'PreventionPackage/PmListFiltersModule/components/PmListFiltersModal';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { knmPmCreateFromModal } from 'KnmPmPackage/MainModule/actions/knmPmActions';

import { preventionListExportPreventions } from '../actions/preventionActions';
import styles from './PreventionPanel.module.scss';
import { CreatePreventionModal } from './create/CreatePreventionModal';
import { ICreatePreventionForm } from '../interfaces/ICreatePreventionForm';

interface IPreventionPanelProps {
  columns: ITableColumn<IKnmRaw>[];
  dataCount: number;
  itemsCheck: TableCheckItems;
  knms: IKnmRaw[];
  onDeleteButton: () => void;
  onSearch: () => void;
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
}

interface IPreventionPanelStoreProps {
  activePmFilter: IPmListFilter | undefined;
  isOmbudsman: boolean;
  isSupervisor: boolean;
  isProsecutor: boolean;
  isTestUser: boolean;
  isErknm: boolean;
}

const PreventionPanelWithoutStore = ({
  columns,
  dataCount,
  itemsCheck,
  knms,
  isErknm,
  onDeleteButton,
  onSearch,
  searchInputValue,
  setSearchInputValue,
  activePmFilter,
  isOmbudsman,
  isSupervisor,
  isProsecutor,
  isTestUser,
}: IPreventionPanelProps & IPreventionPanelStoreProps) => {
  const isFilterCheckboxActive = !!getStorageItem(StorageNamesEnum.PM_FILTER_ON);
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();

  const checkedItemsNumber = Object.keys(itemsCheck).filter(key => itemsCheck[key]).length;
  const [isSearchInputValid, setIsSearchInputValid] = useState(false);
  const [searchInputError, setSearchInputError] = useState('');
  const [shouldShowAddNewPmModal, setShouldShowAddNewPmModal] = useState(false);

  const isDeleteButtonEnabled =
    isSupervisor &&
    checkedItemsNumber > 0 &&
    (isTestUser
      ? knms.every((knm: IKnmRaw) => {
          return (
            (!itemsCheck[String(knm.id)] || knm.statusId === KnmErknmStatusIdsByCodeEnum.FILL_IN_PROCESS) && knm.isTest
          );
        })
      : knms.every((knm: IKnmRaw) => {
          return !itemsCheck[String(knm.id)] || knm.statusId === KnmErknmStatusIdsByCodeEnum.FILL_IN_PROCESS;
        }));

  const onAddClick = useCallback(() => {
    if (isErknm) {
      history.push(ROUTE_PM_NEW);
    } else {
      setShouldShowAddNewPmModal(true);
    }
  }, [history]);

  const handleFilterCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(pmListFilterSetSwitcher(e.target.checked));
      dispatch(preventionGetKnms());
    },
    [dispatch],
  );

  const handleFilterButtonClick = useCallback(() => {
    setIsFiltersModalVisible(true);
  }, []);

  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);
  const closeCreatePmModal = useCallback(() => setShouldShowAddNewPmModal(false), []);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      setSearchInputValue(String(value));
      const isValid = value.length > 2 || value.length === 0;

      setIsSearchInputValid(isValid);
    },
    [setSearchInputValue],
  );

  const handleSearchInputSubmit = useCallback(() => {
    if (!isSearchInputValid) {
      setSearchInputError('Длина строки должна быть не менее 3 символов');
    } else {
      setSearchInputError('');
      onSearch();
    }
  }, [isSearchInputValid, onSearch]);

  const exportNotify = ({ id }: IExport) => dispatch(addNoticeImportNotification(id));

  const handleExportODSClick = useCallback(() => {
    const isFilterOn = !!getStorageItem(StorageNamesEnum.PM_FILTER_ON);
    const currentFilter = activePmFilter && isFilterOn ? mapPmFilterToRaw(activePmFilter) : undefined;

    dispatch(preventionListExportPreventions(exportNotify, ExportFormatsEnum.ODS, columns, currentFilter));
  }, [dispatch, activePmFilter, columns]);

  const handleExportXLSXClick = useCallback(() => {
    const isFilterOn = !!getStorageItem(StorageNamesEnum.PM_FILTER_ON);
    const currentFilter = activePmFilter && isFilterOn ? mapPmFilterToRaw(activePmFilter) : undefined;

    dispatch(preventionListExportPreventions(exportNotify, ExportFormatsEnum.XLSX, columns, currentFilter));
  }, [dispatch, activePmFilter, columns]);

  const handleOnBlur = useCallback(() => {
    !isSearchInputValid ? setSearchInputError('Длина строки должна быть не менее 3 символов') : setSearchInputError('');
  }, [isSearchInputValid]);

  const createPmFromModalHandler = useCallback(
    (values: ICreatePreventionForm) => dispatch(knmPmCreateFromModal(values, history)),
    [dispatch],
  );

  const dropdownItems: IDropdownItem[] = [
    {
      id: 'export-ods-prevention',
      content: <DropdownItem onClick={handleExportODSClick}>Экспорт списка в ODS</DropdownItem>,
    },
    {
      id: 'export-xlsx-prevention',
      content: <DropdownItem onClick={handleExportXLSXClick}>Экспорт списка в XLSX</DropdownItem>,
    },
  ];

  const filterButtonAttributes = {
    ...(isFilterCheckboxActive && { 'data-active': true }),
  };

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        {!isOmbudsman && !isProsecutor && (
          <Button startIcon={styles.AddIcon} text="Добавить" id="addButton" color="primary" onClick={onAddClick} />
        )}
        {isSupervisor && (
          <Button
            className={styles.ButtonDelete}
            startIcon={styles.DeleteIcon}
            text="Удалить"
            id="deleteButton"
            disabled={!isDeleteButtonEnabled}
            onClick={onDeleteButton}
          />
        )}
        <SearchInput
          className={styles.SearchInput}
          value={searchInputValue}
          name="searchString"
          placeholder="Поиск ПМ"
          buttonDisabled={!isSearchInputValid}
          error={searchInputError}
          onChange={handleSearchInputChange}
          onSubmit={handleSearchInputSubmit}
          onBlur={handleOnBlur}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск ПМ по следующим критериям:
              <ul>
                <li>Учетный номер ПМ</li>
                <li>ИНН или ОГРН проверяемого лица</li>
                <li>Наименование проверяемого лица</li>
                <li>Дата начала проведения ПМ</li>
              </ul>
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <p className={styles.CurrentFilter}>
          {'ПМ моей организации'}&nbsp;
          <small className={styles.CurrentFilterNumbers}>({dataCount})</small>
        </p>
        <FilterCheckbox
          className={styles.FilterCheckbox}
          id="filterToggler"
          name="filterToggler"
          checked={isFilterCheckboxActive}
          onChange={handleFilterCheckboxChange}
        />
        <Button
          className={styles.FilterButton}
          text="Фильтр..."
          color="info"
          onClick={handleFilterButtonClick}
          {...filterButtonAttributes}
        />
        <Dropdown items={dropdownItems} />
      </div>
      {isFiltersModalVisible && <PmListFiltersModal visible={isFiltersModalVisible} onClose={closeFiltersModal} />}
      {shouldShowAddNewPmModal && (
        <CreatePreventionModal
          onClose={closeCreatePmModal}
          visible={true}
          onSaveButtonClick={createPmFromModalHandler}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPreventionPanelStoreProps => ({
  activePmFilter: getPmListFilterCurrentFilter(state),
  isOmbudsman: getUserRoleIsOmbudsman(state),
  isProsecutor: getUserRoleIsProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isTestUser: getUserProfileIsTest(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

export const PreventionPanel = connect(mapStateToProps)(PreventionPanelWithoutStore);
